import dayjs from 'dayjs'
import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'

import { getServerErrorMessage, getServerErrorTitle } from 'lib/error'
import Cookie from 'lib/cookie'
import { isSentryWatchedUser } from 'lib/helpers'
import { Notification } from 'lib/_types'
import { idCardCheckApi } from 'store/api/yay/idCardCheck'
import { groupApi } from 'store/api/yay/group'
import { userApi } from 'store/api/yay/user'
import { postApi } from 'store/api/yay/post'
import { timelineApi } from 'store/api/yay/timeline'
import { miscApi } from 'store/api/yay/misc'
import {
  logoutActionType,
  refreshAccessTokenActionType,
} from 'store/ducks/auth'

const initialState: Notification[] = []

const DEFAULT_DURATION = {
  snackbar: 5000,
  dialog: 3000,
}

function isRejectedAction(action: AnyAction) {
  return (
    action.type.endsWith('_FAIL') ||
    (action.type.endsWith('/rejected') &&
      typeof action?.payload?.status !== 'undefined')
  )
}

const notificationsSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    addNotification: (
      state,
      action: PayloadAction<Partial<Notification> & { message: string }>
    ) => {
      const {
        title,
        message,
        duration,
        variant = 'dialog',
        type = 'success',
      } = action.payload

      const id = `${dayjs().format('HH:mm:ss:SSS')}: ${message}`

      state.push({
        id,
        title,
        message,
        variant,
        type,
        duration: duration ?? DEFAULT_DURATION[variant],
      })
    },
    removeNotification: (state, action: PayloadAction<Notification['id']>) => {
      return state.filter((r) => r.id !== action.payload)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(isRejectedAction, (state, action) => {
      const currentUserId = Cookie.userId.get()
      const isWatchedUser = isSentryWatchedUser(currentUserId)

      let reportToSentry = true

      let errorCode =
        action?.error?.response?.data?.error_code ??
        action?.payload?.data?.error_code ??
        action?.payload?.data?.res?.error_code

      // Intercepts certain types of error to not show it with default notification.
      switch (errorCode) {
        case -3: // access token expire
        case -18: // invalid token
        case -29: // recaptcha required
        case -31: // Login requires 2FA.
        case -32: // 2FA code incorrect.
        case -313: // This user wants to talk with mutual follow users only.
        case -343: // Group invitation limit exceeded per non-VIP user, will be handled by confirmboxes.
        case -377:
        case -378:
          return state

        // may still show error notification, but not send data to sentry
        case -202: // User tried to login with invalid password or email
        case 403: // forbidden
        case 404: // not found
        case 429: // request limit
        case -12: // user blocked
        case -363: // view non following private user timeline
        case -367: // only following user action
          reportToSentry = false
          break
      }

      switch (action?.payload?.status) {
        case 403: // forbidden
        case 404: // not found
          reportToSentry = false
          break
      }

      if (action.type === refreshAccessTokenActionType.failure) {
        reportToSentry = isWatchedUser
      }

      // if logout failed, just ignore it
      if (action.type === logoutActionType.failure) {
        return state
      }

      // Intercepts certain types of error to not show it with default notification.
      // You cannot talk with different age.
      if (
        errorCode === -338 &&
        action?.meta?.arg?.endpointName == 'createChatRoom'
      ) {
        return state
      }

      // https://jira.nanameue.jp/browse/YAY-6875
      if (
        errorCode === -367 &&
        action?.meta?.arg?.endpointName == 'createChatRoom'
      ) {
        errorCode = '-367-create-chat'
      }

      if (
        idCardCheckApi.endpoints.submitVerificationCode.matchRejected(action)
      ) {
        if (errorCode === 404) {
          errorCode = '404-wrong-verification-code'
        }
      }

      if (
        errorCode === -405 &&
        groupApi.endpoints.joinGroup.matchRejected(action)
      ) {
        return state
      }

      // Wrong email or any error with email sending
      if (
        userApi.endpoints.emailVerification.matchRejected(action) &&
        errorCode === 400
      ) {
        return state
      }

      // Code and email not matched
      if (
        userApi.endpoints.emailGrantTokens.matchRejected(action) &&
        errorCode === 404
      ) {
        return state
      }

      if (
        postApi.endpoints.getUrlMetaData.matchRejected(action) &&
        errorCode === 403
      ) {
        return state
      }

      // Group not found
      if (
        groupApi.endpoints.getGroup.matchRejected(action) &&
        errorCode === 404
      ) {
        errorCode = '404-group-not-found'
      }

      // Hashtag not found
      if (
        timelineApi.endpoints.getHashtagTimeline.matchRejected(action) &&
        errorCode === 404
      ) {
        return state
      }

      // Invadid group id
      if (
        (timelineApi.endpoints.getGroupTimeline.matchRejected(action) ||
          timelineApi.endpoints.getGroupTimelinePinned.matchRejected(action) ||
          postApi.endpoints.getGroupAlbumList.matchRejected(action)) &&
        errorCode === -1
      ) {
        return state
      }

      // Testnet airdrop
      if (miscApi.endpoints.testnetAirdrop.matchRejected(action)) {
        return state
      }

      // Count meaningful time
      if (userApi.endpoints.usersAlive.matchRejected(action)) {
        return state
      }

      // Invitation code.
      if (userApi.endpoints.usersReferrals.matchRejected(action)) {
        return state
      }

      // Sign up.
      if (userApi.endpoints.signUp.matchRejected(action)) {
        return state
      }

      // 2FA enable code input.
      if (userApi.endpoints.usersSecretEnable.matchRejected(action)) {
        return state
      }

      // 2FA disable code input.
      if (userApi.endpoints.usersSecretDisable.matchRejected(action)) {
        return state
      }

      // Post not found
      if (
        postApi.endpoints.getPost.matchRejected(action) &&
        errorCode === 404
      ) {
        errorCode = '-6'
      }

      // Post not found
      // getPostLikers always call together with getPost, let getPost hamdle the error
      if (
        postApi.endpoints.getPostLikers.matchRejected(action) &&
        errorCode === -6
      ) {
        return state
      }

      if (
        userApi.endpoints.getCurrentUser.matchRejected(action) &&
        errorCode === -1
      ) {
        return state
      }

      const title = getServerErrorTitle(errorCode)
      const message = getServerErrorMessage(errorCode)

      // send error to sentry
      if (reportToSentry) {
        Sentry.captureException(
          new Error(`Error ${action.type} ${errorCode}`),
          {
            extra: {
              errorCode,
              popup: { title, message },
              action: JSON.stringify(action),
            },
          }
        )
      }

      const variant: Notification['variant'] =
        action?.payload?.data?.variant ||
        (errorCode === -405 || errorCode === -347 ? 'dialog' : 'snackbar')

      if (variant === 'alert') {
        return alert(message)
      }

      state.push({
        id: `${errorCode}_ERROR_MESSAGE`,
        title,
        message,
        type: 'error',
        variant,
        duration: DEFAULT_DURATION[variant],
      })
    })
  },
})

export const selectNotifications = (state: any): typeof initialState =>
  state.notifications || []

export const { removeNotification, addNotification } =
  notificationsSlice.actions

export default notificationsSlice.reducer
